/**
 * AGP — bespoke Contact page (2026 redesign handoff, Page 6): page hero, then a
 * two-column band — the message form on warm-white, the Philippines office details
 * on corporate green.
 *
 * The form is the REAL one: it posts to the shared `/storefront/contact` endpoint
 * via `submitContactInquiry`, so inquiries still land in the admin inbox. Only the
 * styling and the field split (first/last name) changed.
 */
import { useState, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { AGP_SITE } from '@mlm/shared';
import { submitContactInquiry } from '../../../lib/api';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderLines } from '../../../storefront/lib/rich-paras';
import { AgpShell } from './shell';
import { GREEN_CORP } from './tokens';

export function AgpContact(): JSX.Element {
  const sc = useSiteContent(AGP_SITE);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      // The shared contact endpoint takes name/email/subject/message — join the
      // split name fields and fold the phone into the message so neither is lost.
      await submitContactInquiry({
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        subject: form.subject,
        message: form.phone ? `Phone: ${form.phone}\n\n${form.message}` : form.message,
      });
      setSent(true);
    } catch {
      setErr('Sorry, something went wrong. Please try again or email us directly.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AgpShell active="contact">
      <div className="agp-page-hero">
        <div className="agp-label">{sc.text('contact.hero.label')}</div>
        <div className="agp-rule centered" />
        <h1>{renderLines(sc.text('contact.hero.title'))}</h1>
        <p>{sc.text('contact.hero.sub')}</p>
      </div>

      <div className="agp-contact">
        <div className="agp-contact-form">
          {sent ? (
            <div className="agp-form-sent">
              <CheckCircle2 size={56} color={GREEN_CORP} />
              <h2>{sc.text('contact.form.sentTitle')}</h2>
              <p>{sc.text('contact.form.sentBody')}</p>
            </div>
          ) : (
            <>
              <div className="agp-label">{sc.text('contact.form.label')}</div>
              <div className="agp-rule" />
              <h2 className="agp-title sm" style={{ marginBottom: 12 }}>
                {sc.text('contact.form.title')}
              </h2>
              <p className="agp-lead" style={{ marginBottom: 44 }}>
                {sc.text('contact.form.lead')}
              </p>

              <form onSubmit={onSubmit}>
                <div className="agp-form-row">
                  <div className="agp-form-group">
                    <label className="agp-form-label" htmlFor="agp-first">
                      First Name
                    </label>
                    <input
                      id="agp-first"
                      className="agp-input"
                      required
                      value={form.firstName}
                      onChange={set('firstName')}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="agp-form-group">
                    <label className="agp-form-label" htmlFor="agp-last">
                      Last Name
                    </label>
                    <input
                      id="agp-last"
                      className="agp-input"
                      required
                      value={form.lastName}
                      onChange={set('lastName')}
                      placeholder="dela Cruz"
                    />
                  </div>
                </div>

                <div className="agp-form-group">
                  <label className="agp-form-label" htmlFor="agp-email">
                    Email Address
                  </label>
                  <input
                    id="agp-email"
                    className="agp-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set('email')}
                    placeholder="your@email.com"
                  />
                </div>

                <div className="agp-form-group">
                  <label className="agp-form-label" htmlFor="agp-phone">
                    Phone Number
                  </label>
                  <input
                    id="agp-phone"
                    className="agp-input"
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="+63 917 000 0000"
                  />
                </div>

                <div className="agp-form-group">
                  <label className="agp-form-label" htmlFor="agp-subject">
                    Subject
                  </label>
                  <input
                    id="agp-subject"
                    className="agp-input"
                    required
                    value={form.subject}
                    onChange={set('subject')}
                    placeholder="Product inquiry / Distributorship / Other"
                  />
                </div>

                <div className="agp-form-group">
                  <label className="agp-form-label" htmlFor="agp-message">
                    Message
                  </label>
                  <textarea
                    id="agp-message"
                    className="agp-input"
                    required
                    value={form.message}
                    onChange={set('message')}
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                {err && <div className="agp-form-error">{err}</div>}

                <button
                  type="submit"
                  className="agp-btn agp-btn-green"
                  style={{ marginTop: 8, width: '100%' }}
                  disabled={busy}
                >
                  {busy ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="agp-contact-info">
          <div className="agp-label">{sc.text('contact.office.label')}</div>
          <div className="agp-rule" />
          <div className="agp-contact-country">{sc.text('contact.office.country')}</div>

          <div className="agp-contact-detail">
            <div className="agp-contact-detail-label">Address</div>
            <div className="agp-contact-detail-value">
              {sc.text('contact.office.address')}
              <br />
              {sc.text('contact.office.addressLine2')}
            </div>
          </div>
          <div className="agp-contact-detail">
            <div className="agp-contact-detail-label">Phone</div>
            <div className="agp-contact-detail-value">
              <a href={`tel:${sc.text('contact.office.phone')}`}>
                {sc.text('contact.office.phone')}
              </a>
            </div>
          </div>
          <div className="agp-contact-detail">
            <div className="agp-contact-detail-label">Email</div>
            <div className="agp-contact-detail-value">
              <a href={`mailto:${sc.text('contact.office.email')}`}>
                {sc.text('contact.office.email')}
              </a>
            </div>
          </div>
          <div className="agp-contact-detail">
            <div className="agp-contact-detail-label">Website</div>
            <div className="agp-contact-detail-value">{sc.text('contact.office.website')}</div>
          </div>
          <div className="agp-contact-detail">
            <div className="agp-contact-detail-label">Hours</div>
            <div className="agp-contact-detail-value">
              {sc.paras('contact.office.hours').map((h) => (
                <div key={h}>{h}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AgpShell>
  );
}
